import { Heading } from "@/components/Heading"
import { InfoCard } from "@/components/InfoCard"
import { Box } from "@/components/Box"
import { DocumentIcon, InformationCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Button } from "@/components/Button"
import useFormContext from '@/context/useFormContext'
import { useContext, useState, useEffect, useRef } from 'react'
import { apiUpload } from '@/services/apiService'
import { useLocation, useNavigate } from 'react-router'
import { DealerContext } from '@/context/DealerContext'
import { Header } from '@/components/HeaderHome'
import { ROUTES } from '@/config/routes'

interface UploadResult {
  file: {
    id: string;
    name: string;
    size: number;
    status: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
    file: File;
  };
  success: boolean;
}

function EndApplication() {
  const { state, dispatch } = useFormContext()
  const navigate = useNavigate()
  const { files, isCheckedAplyID, applyidResponseSuccess } = useLocation().state || {}
  
  // Store necessary data before resetting
  const formIdRef = useRef<string | null>(state.formId)
  const userDataRef = useRef({
    firstName: state.business.aboutYou.firstName,
    lastName: state.business.aboutYou.lastName,
    phoneNumber: state.business.aboutYou.phone
  })
  
  const [uploadResults, setUploadResults] = useState<UploadResult[]>(files || [])
  const [retryingFiles, setRetryingFiles] = useState<Set<string>>(new Set())

  // Intercept browser back button and redirect to form beginning
  useEffect(() => {
    let hasNavigated = false

    const handlePopState = () => {
      if (!hasNavigated) {
        hasNavigated = true
        // Reset form and redirect to beginning
        dispatch({ type: 'RESET' })
        navigate(ROUTES.BUSINESS.ROOT.path, { replace: true })
      }
    }

    // Add a dummy history entry so back button triggers popstate
    window.history.pushState(null, '', window.location.pathname)
    
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [dispatch, navigate])

  // Reset form when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Reset form when leaving the end page
      dispatch({ type: 'RESET' })
    }
  }, [dispatch])

  const handleRetry = async (file: UploadResult['file']) => {
    setRetryingFiles(prev => new Set([...prev, file.id]))
    
    try {
      const formData = new FormData()
      formData.append('file', file.file)
      const uploadResponse = await apiUpload(`Form/UploadDocument?formId=${formIdRef.current}`, formData)
      
      if (uploadResponse.success) {
        setUploadResults(prev => prev.map(result => 
          result.file.id === file.id ? { ...result, success: true } : result
        ))
      } else {
        throw new Error('Upload failed')
      }
    } catch (error) {
      console.error('Error retrying upload:', error)
    } finally {
      setRetryingFiles(prev => {
        const newSet = new Set(prev)
        newSet.delete(file.id)
        return newSet
      })
    }
  }

  const successfulUploads = uploadResults.filter(result => result.success).length
  const failedUploads = uploadResults.filter(result => !result.success).length
  const hasUploads = uploadResults.length > 0

  const dealerContext = useContext(DealerContext)

  // Use stored data instead of state
  const { firstName, lastName, phoneNumber } = userDataRef.current

  return (
    <div>
      <Header />
      <section className='pb-4'>
        <Heading>Thank you <span className='font-bold'>{firstName} {lastName}</span> for your application</Heading>
        { isCheckedAplyID && (
           <Box className='mb-10'>
              {applyidResponseSuccess ? (
                <div className='flex items-center text-[var(--brand-1)] gap-10 relative'>
                  <ExclamationCircleIcon aria-hidden={true} className='absolute -top-0.5 left-0 size-8 shrink-0' />
                  <div className='space-y-2 pl-12 sm:pl-16'>
                    <p className='text-xl font-[var(--font-weight-bold)]'>{firstName}, ready to verify your ID with APLYiD?</p>
                    <span className='block text-lg text-[var(--theme-text)] translate-y-2'>You will receive an SMS on {phoneNumber} with how to proceeed shortly</span>
                  </div>
                </div>
              ) : (
                <div className='flex items-center text-[var(--const-error)] gap-10 relative'>
                  <ExclamationTriangleIcon aria-hidden={true} className='absolute -top-0.5 left-0 size-8 shrink-0' />
                  <div className='space-y-2 pl-12 sm:pl-16'>
                    <p className='text-xl font-[var(--font-weight-bold)]'>{firstName}, we seem to have had a bit of a hiccup connecting to APLYiD?</p>
                    <span className='block text-lg text-[var(--theme-text)] translate-y-2'>Don't worry, one of our team will send you an SMS to verify your ID shortly.</span>
                  </div>
                </div>
              )}
          </Box>
          )
        }
        {hasUploads && (
          <Box className='mb-10'>
            <div className='flex items-center text-[var(--brand-1)] gap-10'>
              <InformationCircleIcon aria-hidden={true} className='size-8 shrink-0' />
              <div className='space-y-2'>
                <p className='text-xl font-[var(--font-weight-bold)]'>File upload completed</p>
                <span className='text-lg text-[var(--theme-text)]'>{successfulUploads}/{uploadResults.length} files were uploaded successfully</span>
              </div>
            </div>
            {failedUploads > 0 && (
              <>
              <p className='text-[var(--theme-danger)] font-[var(--font-weight-bold)] text-lg'>There was an error while uploading {failedUploads} of your files</p>
            
              <div className='space-y-3'>
                {uploadResults.filter(result => !result.success).map((result) => (
                  <div key={result.file.id} className='flex items-center justify-between gap-6'>
                    <div className='grow flex items-center justify-between gap-6 px-5 py-4 rounded-[4px] bg-[var(--theme-danger-bg)] text-[var(--theme-danger)] text-sm/5'>
                      <div className='flex items-center gap-6 grow pr-5'>
                        <DocumentIcon aria-hidden={true} className='size-5 stroke-2 shrink-0' />
                        <div>
                          <p>
                            {result.file.name}<br />
                            <span className='font-bold'>{(result.file.size / (1024 * 1024)).toFixed(2)}mb</span>
                          </p>
                          {result.file.error && <p>{result.file.error}</p>}
                        </div>
                      </div>
                    </div>
                    <div className='shrink-0 flex items-center'>
                      <Button 
                        icon='none' 
                        iconPosition='left' 
                        style='text' 
                        loadingText='uploading' 
                        loading={retryingFiles.has(result.file.id)}
                        onClick={() => handleRetry(result.file)}
                      >
                        Retry
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className='text-lg'><span className='font-bold'>Note:</span> Your application will still be processed</p>
              </>
            )}
          </Box>
        )}
        <p className='pb-12 lg:pb-16 text-center max-w-4xl mx-auto text-2xl md:text-3xl text-[var(--theme-heading-text)] leading-[1.2]'>Next steps</p>
        <div className='max-w-6xl mx-auto justify-center flex flex-col md:flex-row max-md:items-center gap-2 md:gap-8'>
          <InfoCard icon={dealerContext?.dealerThemeIcon?.email || '/icons/info-card/email.svg'} iconDescription='email' align='top'>
            <p>You will receive a confirmation email shortly</p>
          </InfoCard>
          <InfoCard icon={dealerContext?.dealerThemeIcon?.timeToProcess || '/icons/info-card/time-to-process.svg'} iconDescription='Time to process' align='top'>
            <p>We will process your application in the next business day</p>
          </InfoCard>
          <InfoCard icon={dealerContext?.dealerThemeIcon?.contact || '/icons/info-card/contact.svg'} iconDescription='Contact' align='top'>
            <p>
              If you have any questions contact us by phone on<br />
              <span className='font-[var(--font-weight-bold)]'>{dealerContext?.dealerInformation?.phoneNumber || '0800 322 888'}</span>
            </p>
          </InfoCard>
        </div>
      </section>
    </div>
  )
}

export default EndApplication
